import { IconNames } from '@blueprintjs/icons';
import { type BenefitType, BenefitTypeDisplayTextMap } from '@strata/constants';
import { formatDate, formatNameLastFirst, useDebouncedFunction } from '@strata/helper-functions';
import { ReferralChatIcon } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    ProductCell,
    SecondaryData,
} from '@strata/react-grid';
import { PatientInfoList } from '@strata/react-label-value-pairs';
import { Icon } from '@strata/react-status-icons';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, useState } from 'react';
import { useNavigate } from 'react-router';

import { hasGrandfatheredReferralsInFilterOptions } from 'constants/grandfatheredReferral';
import { useUnreadCounts } from 'services/APIHooks';
import { AppRoutes } from 'services/routes';
import { type ReferralConversationCounts } from 'services/userSupportHelpers';
import {
    useGetPagedReferralsQuery,
    useGetReferralFilterOptionsQuery,
    useGetReferralsCountQuery,
    type WonReferralSummary,
} from 'store/api/performanceApi';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties: ComponentProps<
    typeof GridWithFilters<WonReferralSummary>
>['sortProperties'] = [{ id: 'outreach_date', desc: false }];

const columns = ({
    referralConversationCounts,
    showGrandfathered,
}: {
    referralConversationCounts?: ReferralConversationCounts;
    showGrandfathered: boolean;
}): ColumnDef<WonReferralSummary>[] => [
    {
        accessorKey: 'product_name',
        header: 'Product',
        size: 190,
        cell: ({ row, getValue }) => (
            <ProductCell product={getValue()} isBrand={row.original.is_brand} primary />
        ),
    },
    {
        accessorKey: 'last_name',
        header: 'Patient',
        size: 120,
        cell: ({ row, getValue }) => (
            <SecondaryData data={formatNameLastFirst(getValue(), row.original.first_name)} />
        ),
    },
    {
        accessorKey: 'member_id',
        header: 'Member ID',
        size: 120,
    },
    {
        accessorKey: 'segment_name',
        header: 'Health Plan',
        size: 120,
    },
    {
        accessorKey: 'value_cases',
        header: 'Attributes',
        size: 80,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <PatientInfoList
                values={[
                    ...(getValue() ?? []),
                    ...(row.original.has_copay_assistance
                        ? [{ strat_rule_name: PatientInfoList.Types.COPAY }]
                        : []),
                    ...(showGrandfathered && row.original.grandfathered
                        ? [{ strat_rule_name: PatientInfoList.Types.GRANDFATHERED }]
                        : []),
                ]}
                incentiveTotal={row.original.incentive_total}
                iconsOnly
            />
        ),
    },
    {
        accessorKey: 'referral_id',
        header: () => (
            <Icon
                icon={IconNames.CHAT}
                tooltipContent="Open conversations and unread messages indicator."
            />
        ),
        enableSorting: false,
        size: 40,
        cell: ({ getValue }) => (
            <ReferralChatIcon
                openConversations={referralConversationCounts?.[getValue()]?.open}
                unreadMessages={referralConversationCounts?.[getValue()]?.unread}
            />
        ),
    },
    {
        accessorKey: 'outreach_date',
        header: 'Outreach Start Date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'referral_start',
        header: 'Servicing Start Date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'referral_status',
        header: 'Outreach Status',
        size: 130,
        cell: ({ getValue }) => <ReferralStatusIndicator status={getValue()} />,
    },
];

export const UpcomingReferralsListPage = () => {
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();
    const { showGrandfatheredPharmacy } = useFlags<{ showGrandfatheredPharmacy: boolean }>();

    const { referralConversationCounts } = useUnreadCounts();

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(Grids.UPCOMING_REFERRALS, initialSortProperties);

    // The search box updates `search` on every keystroke to keep the input responsive, but the
    // referrals queries are expensive, so only feed the API a debounced copy — otherwise each
    // keystroke fires a concurrent query and saturates the DB.
    const debounceSearchText = useDebouncedFunction();
    const [debouncedSearchText, setDebouncedSearchText] = useState(search);

    const apiQueryParams = {
        page,
        searchText: debouncedSearchText,
        sort,
        upcoming: true,
        ...activeFilters,
    };
    const countQueryParams = {
        searchText: debouncedSearchText,
        upcoming: true,
        ...activeFilters,
    };

    // Count is loaded asynchronously so the grid is not blocked waiting for totalCount.
    const { data: countData } = useGetReferralsCountQuery(countQueryParams);
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetPagedReferralsQuery(apiQueryParams);
    const { data: filterOptions, isFetching: isFilterOptionsFetching } =
        useGetReferralFilterOptionsQuery({ upcoming: true });

    const { data: segmentsData } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segmentsData ?? {};

    const filterCategories: FilterCategory<WonReferralSummary>[] = [
        {
            key: 'segment_id',
            title: 'Health Plan',
            customValues: filterOptions?.segment_id?.map((value) => ({
                value,
                text: payerSegmentMap?.[value]?.name,
            })),
        },
        {
            key: 'product_name',
            title: 'Product',
            customValues: filterOptions?.product_name?.map((value) => ({
                value,
            })),
        },
        {
            key: 'benefit',
            title: 'Benefit',
            customValues: filterOptions?.benefit?.map((value) => ({
                value,
                text: BenefitTypeDisplayTextMap[value as BenefitType],
            })),
        },
        {
            key: 'referral_status',
            title: 'Outreach Status',
            customValues: filterOptions?.referral_status?.map((value) => ({
                value,
                text: ReferralStatusIndicator.StatusMessages[
                    value as (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses]
                ],
            })),
        },
        ...(showGrandfatheredPharmacy && hasGrandfatheredReferralsInFilterOptions(filterOptions)
            ? [
                  {
                      key: 'grandfathered' as const,
                      title: 'Grandfathered Referral',
                      customValues: [
                          { value: 'true', text: 'Grandfathered' },
                          { value: 'false', text: 'Non-Grandfathered' },
                      ],
                  },
              ]
            : []),
    ];

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Upcoming Referrals"
                secondaryTitle={loggedInSupplierName}
                activeFilters={activeFilters}
                columns={columns({
                    referralConversationCounts,
                    showGrandfathered: showGrandfatheredPharmacy,
                })}
                currentPage={page}
                data={currentPageData?.referrals}
                enableSearch
                filterCategories={filterCategories}
                filterPaneRenderWidth={filterPaneWidth}
                isLoading={
                    isCurrentPageDataFetching ||
                    isFilterOptionsFetching ||
                    search !== debouncedSearchText
                }
                manualSorting
                onFilter={updateGridActiveFilters}
                onFilterPaneResize={updateFilterPaneWidth}
                onPageChange={updateCurrentPage}
                onRowClick={(rowData) =>
                    navigate(AppRoutes.wonReferralDetails(rowData.referral_id))
                }
                onSearch={(value) => {
                    updateGridSearch?.(value);
                    debounceSearchText(() => setDebouncedSearchText(value));
                }}
                onSort={updateGridSort}
                rowCssClass={(rowData) =>
                    classNames({ 'griddle-row--disabled': dayjs().isBefore(rowData.outreach_date) })
                }
                searchText={search}
                sortProperties={sort}
                totalCount={countData}
            />
        </FormPageContainer>
    );
};
